import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { duplicateVlanIds, isValidIpv4Cidr, type VlanDef } from '@system-atlas/shared';
import { Button, Label, TextInput } from '../components/ui';

interface VlanManagerProps {
  vlans: VlanDef[];
  onAdd: (vlan: VlanDef) => void;
  onUpdate: (id: string, patch: Partial<VlanDef>) => void;
  onRemove: (id: string) => void;
  onClose: () => void;
}

const DEFAULT_COLOR = '#3b82f6';
const INVALID_SUBNET = 'border-red-500 focus:border-red-500 focus:ring-red-400/40';

/** Nicht leer und kein gueltiges IPv4-CIDR? */
function subnetInvalid(value: string | undefined): boolean {
  return (value ?? '') !== '' && !isValidIpv4Cidr(value ?? '');
}

export function VlanManager({ vlans, onAdd, onUpdate, onRemove, onClose }: VlanManagerProps) {
  const { t } = useTranslation();
  const [vlanId, setVlanId] = useState('');
  const [name, setName] = useState('');
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [subnet, setSubnet] = useState('');
  const dups = duplicateVlanIds(vlans);
  const anyInvalidSubnet = subnetInvalid(subnet) || vlans.some((v) => subnetInvalid(v.subnet));

  const addDisabled = vlanId.trim() === '' || Number.isNaN(Number(vlanId));

  const handleAdd = () => {
    if (addDisabled) return;
    const trimmedSubnet = subnet.trim();
    onAdd({
      id: globalThis.crypto?.randomUUID?.() ?? `vlan-${Date.now()}`,
      vlanId: Number(vlanId),
      name: name.trim(),
      color,
      ...(trimmedSubnet === '' ? {} : { subnet: trimmedSubnet }),
    });
    setVlanId('');
    setName('');
    setColor(DEFAULT_COLOR);
    setSubnet('');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t('editor.vlanManager.title')}
      onMouseDown={onClose}
    >
      <div
        className="max-h-[80vh] w-full max-w-2xl overflow-auto rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{t('editor.vlanManager.title')}</h2>
          <Button variant="ghost" className="px-2" onClick={onClose} aria-label={t('common.close')}>
            ✕
          </Button>
        </div>

        {/* Bestandsliste */}
        {vlans.length === 0 ? (
          <p className="mb-4 text-sm text-slate-500 dark:text-slate-400">{t('editor.vlanManager.empty')}</p>
        ) : (
          <>
            {/* Spaltenueberschriften, exakt ueber den Eingabefeldern ausgerichtet */}
            <div className="mb-1.5 flex items-center gap-2 px-0.5 text-xs font-medium text-slate-500 dark:text-slate-400">
              <span className="w-10 shrink-0" aria-hidden="true" />
              <span className="w-20 shrink-0">{t('editor.vlanManager.vlanId')}</span>
              <span className="flex-1">{t('editor.vlanManager.name')}</span>
              <span className="w-36 shrink-0">{t('editor.vlanManager.subnet')}</span>
              <span className="w-16 shrink-0" aria-hidden="true" />
            </div>
            <ul className="mb-4 space-y-2">
              {vlans.map((v) => (
                <li key={v.id} className="flex items-center gap-2">
                  <input
                    type="color"
                    className="h-8 w-10 shrink-0 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
                    value={v.color}
                    onChange={(e) => onUpdate(v.id, { color: e.target.value })}
                    aria-label={t('editor.vlanManager.color')}
                  />
                  <TextInput
                    className="w-20 shrink-0"
                    type="number"
                    value={String(v.vlanId)}
                    onChange={(e) => onUpdate(v.id, { vlanId: Number(e.target.value) })}
                    aria-label={t('editor.vlanManager.vlanId')}
                  />
                  <TextInput
                    className="flex-1"
                    value={v.name}
                    onChange={(e) => onUpdate(v.id, { name: e.target.value })}
                    aria-label={t('editor.vlanManager.name')}
                  />
                  <TextInput
                    className={`w-36 shrink-0 font-mono${subnetInvalid(v.subnet) ? ` ${INVALID_SUBNET}` : ''}`}
                    value={v.subnet ?? ''}
                    placeholder={t('editor.vlanManager.subnetPlaceholder')}
                    onChange={(e) => onUpdate(v.id, { subnet: e.target.value })}
                    aria-label={t('editor.vlanManager.subnet')}
                  />
                  <div className="flex w-16 shrink-0 justify-end">
                    <Button variant="danger" className="px-2 py-1 text-xs" onClick={() => onRemove(v.id)}>
                      {t('editor.delete')}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {dups.length > 0 && (
          <p className="mb-3 text-xs text-amber-600 dark:text-amber-400">
            {t('editor.vlanManager.duplicateWarning', { ids: dups.join(', ') })}
          </p>
        )}
        {anyInvalidSubnet && (
          <p className="mb-3 text-xs text-red-600 dark:text-red-400">
            {t('editor.vlanManager.invalidCidr')}
          </p>
        )}

        {/* Neues VLAN */}
        <div className="flex items-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-700">
          <div>
            <Label htmlFor="new-vlan-id">{t('editor.vlanManager.vlanId')}</Label>
            <TextInput
              id="new-vlan-id"
              className="w-20"
              type="number"
              value={vlanId}
              onChange={(e) => setVlanId(e.target.value)}
            />
          </div>
          <div className="flex-1">
            <Label htmlFor="new-vlan-name">{t('editor.vlanManager.name')}</Label>
            <TextInput id="new-vlan-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="w-36">
            <Label htmlFor="new-vlan-subnet">{t('editor.vlanManager.subnet')}</Label>
            <TextInput
              id="new-vlan-subnet"
              className={`font-mono${subnetInvalid(subnet) ? ` ${INVALID_SUBNET}` : ''}`}
              value={subnet}
              placeholder={t('editor.vlanManager.subnetPlaceholder')}
              onChange={(e) => setSubnet(e.target.value)}
            />
          </div>
          <input
            type="color"
            className="h-9 w-10 shrink-0 cursor-pointer rounded border border-slate-300 dark:border-slate-600"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            aria-label={t('editor.vlanManager.color')}
          />
          <Button onClick={handleAdd} disabled={addDisabled}>
            {t('editor.vlanManager.add')}
          </Button>
        </div>
      </div>
    </div>
  );
}
